import { useCallback, useEffect, useState } from "react";
import { Gift, Sparkles } from "lucide-react";
import { api, fileSrc, formatApiError } from "@/lib/api";
import { PageShell } from "@/components/site/PageShell";
import { Reveal } from "@/components/site/Reveal";
import { LogoMark } from "@/components/site/Logo";
import { RetryImg } from "@/components/site/RetryImg";
import { useSeo } from "@/components/site/Seo";
import { PackOpening } from "@/components/gacha/PackOpening";

const PrizeRow = ({ p, i, zonk }) => (
  <div data-testid={zonk ? "gacha-prize-zonk" : `gacha-prize-${i}`} className={`flex items-center gap-4 rounded-2xl border p-3 md:p-4 ${zonk ? "border-white/10 bg-ink" : "border-volt/25 bg-surface"}`}>
    <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-ink md:h-20 md:w-20">
      {p.image_url ? <RetryImg src={fileSrc(p.image_url)} alt={p.name} className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center"><LogoMark className="h-8 w-8 opacity-50" /></div>}
    </div>
    <p className="min-w-0 flex-1 font-display text-base font-semibold text-paper md:text-lg">{p.name}</p>
  </div>
);

const RedeemBar = ({ onResult }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/gacha/redeem", { code });
      setCode("");
      onResult(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} data-testid="gacha-redeem-form" className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-ink/85 px-5 py-4 backdrop-blur-xl">
      <div className="mx-auto flex max-w-3xl flex-col gap-2">
        <div className="flex gap-2">
          <input data-testid="gacha-code-input" required value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Kode unik dari CS" className="min-w-0 flex-1 rounded-full border border-white/15 bg-surface px-4 py-3.5 font-mono text-sm text-paper outline-none placeholder:font-sans focus:border-volt sm:px-5 sm:text-base" />
          <button data-testid="gacha-open-button" disabled={busy} className="flex shrink-0 items-center gap-2 rounded-full bg-volt px-6 py-3.5 font-semibold text-ink transition-transform hover:scale-[1.03] disabled:opacity-60">
            <Gift className="h-4 w-4" /> {busy ? "…" : "Buka Pack"}
          </button>
        </div>
        {error ? <p data-testid="gacha-error" className="px-2 text-sm text-red-400">{error}</p> : null}
      </div>
    </form>
  );
};

export default function Gacha() {
  const [data, setData] = useState(null);
  const [result, setResult] = useState(null);
  useSeo({ title: "Brewek — Event Hadiah Gratis", description: "Event Brewek BagiPulsa: tukar kode unik dari CS dan buka pack untuk hadiah voucher gratis.", path: "/brewek", noindex: true });
  const load = useCallback(() => {
    api.get("/gacha").then((r) => setData(r.data || { enabled: false })).catch(() => setData({ enabled: false }));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const prizes = Array.isArray(data?.prizes) ? data.prizes : [];
  return (
    <PageShell testId="gacha-page" footer={false}>
      <section className="mx-auto min-h-[calc(100svh-7rem)] max-w-3xl px-5 pb-40 md:min-h-[calc(100svh-9rem)]">
        {data && !data.enabled ? (
          <div data-testid="gacha-closed" className="py-20 text-center">
            <LogoMark className="mx-auto h-14 w-14" />
            <h1 className="mt-6 font-display text-3xl font-bold sm:text-4xl">Belum ada event brewek</h1>
            <p className="mt-3 text-zinc-400">Pantau terus pengumuman dari BagiPulsa, ya!</p>
          </div>
        ) : null}
        {data?.enabled ? (
          <>
            <Reveal>
              <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-zinc-400"><span className="h-2 w-2 animate-pulse rounded-full bg-lime" /> Event berlangsung</p>
              <h1 data-testid="gacha-title" className="mt-5 font-display text-4xl font-bold leading-[1] tracking-tight sm:text-5xl lg:text-6xl">{data.title}</h1>
              {data.subtitle ? <p className="mt-5 text-zinc-300">{data.subtitle}</p> : null}
            {data.test_mode ? <p data-testid="gacha-test-banner" className="mt-6 rounded-2xl border border-lime/40 bg-lime/10 px-4 py-3 text-sm text-lime">Mode uji coba aktif: masukkan kode apa saja untuk mencoba. Voucher yang keluar tidak dianggap terpakai dan kode tidak terpakai.</p> : null}
            </Reveal>
            <h2 className="mt-12 flex items-center gap-2 font-display text-xl text-paper">Hadiah yang bisa kamu dapetin <Sparkles className="h-5 w-5 text-volt" /></h2>
            <div className="mt-5 space-y-3" data-testid="gacha-prize-list">
              {prizes.map((p, i) => <PrizeRow key={p.id} p={p} i={i} />)}
              {data.zonk ? <PrizeRow p={data.zonk} zonk /> : null}
            </div>
          </>
        ) : null}
      </section>
      {data?.enabled ? <RedeemBar onResult={setResult} /> : null}
      {result ? <PackOpening result={result} title={data?.title} onClose={() => { setResult(null); load(); }} /> : null}
    </PageShell>
  );
}
